import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, TrendingUp, Users, Clock } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import api from "../../lib/api";

export default function AdminDashboard() {
  const { t, lang } = useLanguage();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const load = () => {
      api.get("/admin/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    };
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div data-testid="admin-dashboard-page">
      <h1 className="font-serif text-2xl sm:text-3xl text-white mb-8">{t("admin.dashboard.title")}</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard testId="stat-today" icon={CalendarCheck} label={t("admin.dashboard.todayAppointments")} value={stats?.today_count ?? "-"} />
        <StatCard testId="stat-upcoming" icon={Clock} label={t("admin.dashboard.upcoming")} value={stats?.upcoming_count ?? "-"} />
        <StatCard testId="stat-total" icon={Users} label={t("admin.dashboard.totalBookings")} value={stats?.total_bookings ?? "-"} />
        <StatCard testId="stat-revenue" icon={TrendingUp} label={t("admin.dashboard.revenueMonth")} value={stats ? `€${stats.revenue_month.toFixed(0)}` : "-"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <Link to="/admin/boekingen" data-testid="quick-action-new-booking" className="bg-[#141414] border border-white/10 rounded-lg p-5 hover:border-gold/40 transition-colors">
          <p className="text-gold text-sm">{t("admin.dashboard.newBooking")}</p>
        </Link>
        <Link to="/admin/agenda" data-testid="quick-action-calendar" className="bg-[#141414] border border-white/10 rounded-lg p-5 hover:border-gold/40 transition-colors">
          <p className="text-gold text-sm">{t("admin.dashboard.viewCalendar")}</p>
        </Link>
        <Link to="/admin/diensten" data-testid="quick-action-services" className="bg-[#141414] border border-white/10 rounded-lg p-5 hover:border-gold/40 transition-colors">
          <p className="text-gold text-sm">{t("admin.dashboard.manageServices")}</p>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div data-testid="admin-today-panel" className="bg-[#141414] border border-gold/20 rounded-lg p-5">
          <h2 className="text-white text-lg mb-4 flex items-center gap-2">
            <CalendarCheck size={18} className="text-gold" /> {t("admin.dashboard.todayPanelTitle")}
          </h2>
          {stats && stats.today_appointments.length === 0 && (
            <p data-testid="no-appointments-today-message" className="text-white/40 text-sm">{t("admin.dashboard.noAppointmentsToday")}</p>
          )}
          <div className="divide-y divide-white/5">
            {stats && stats.today_appointments.map((a) => (
              <div key={a.id} data-testid={`today-appointment-${a.id}`} className="flex items-center justify-between py-3 text-sm">
                <div>
                  <p className="text-white">{a.customer_name}</p>
                  <p className="text-white/40 text-xs">{lang === "nl" ? a.service_name_nl : a.service_name_en}</p>
                </div>
                <p className="text-gold font-semibold text-base">{a.start_time}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#141414] border border-white/10 rounded-lg p-5">
          <h2 className="text-white text-lg mb-4">{t("admin.dashboard.upcomingAppointments")}</h2>
          {stats && stats.upcoming_appointments.length === 0 && (
            <p data-testid="no-upcoming-message" className="text-white/40 text-sm">{t("admin.dashboard.noUpcoming")}</p>
          )}
          <div className="divide-y divide-white/5">
            {stats && stats.upcoming_appointments.map((a) => (
              <div key={a.id} data-testid={`dashboard-appointment-${a.id}`} className="flex items-center justify-between py-3 text-sm">
                <div>
                  <p className="text-white">{a.customer_name}</p>
                  <p className="text-white/40 text-xs">{lang === "nl" ? a.service_name_nl : a.service_name_en}</p>
                </div>
                <div className="text-right">
                  <p className="text-white/80">{a.date}</p>
                  <p className="text-gold text-xs">{a.start_time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div data-testid={testId} className="bg-[#141414] border border-white/10 rounded-lg p-5">
    <Icon size={18} className="text-gold mb-3" />
    <p className="text-2xl text-white font-serif">{value}</p>
    <p className="text-white/40 text-xs mt-1">{label}</p>
  </div>
);
